<template>
  <div>
    <div class="login-header">
      <h2>{{ needsSetup ? t('totpSetupTitle') : t('totpTitle') }}</h2>
      <button class="close-btn" @click="$emit('cancel')" :aria-label="t('cancel')">&times;</button>
    </div>

    <div class="form">

      <div v-if="loadingSetup" class="loading">{{ t('loading') }}</div>

      <template v-else-if="needsSetup && setupUrl">
        <p class="hint">{{ t('totpSetupHint') }}</p>
        <!--
          For the QR image we hand the otpauth:// URL to a public QR
          renderer. This keeps the bundle tiny — no client-side QR
          library required. Replace with a self-hosted renderer if
          the engine ever fronts one.
        -->
        <div class="qr-wrap">
          <img :src="qrImageUrl" :alt="t('totpSetupTitle')" />
        </div>
        <div class="otpauth-fallback">
          <small>{{ setupUrl }}</small>
        </div>
      </template>

      <p v-else class="hint">{{ t('totpEnterCode') }}</p>

      <input
        v-model="code"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        :placeholder="t('totpCode')"
        @keyup.enter="submit"
      />

      <div v-if="localError || errorMessage" class="error-message">
        {{ localError || errorMessage }}
      </div>

      <button :disabled="!canSubmit || busy || working" @click="submit">
        {{ t('totpVerify') }}
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from '../i18n/index.js'
import LoginApiService from '../services/LoginApiService.js'

export default defineComponent({
  name: 'TotpView',

  props: {
    session: { type: Object, default: null },
    busy: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' }
  },

  emits: ['verified', 'cancel'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      code: '',
      localError: '',
      working: false,
      loadingSetup: true,
      needsSetup: false,
      setupUrl: ''
    }
  },

  computed: {
    canSubmit() {
      // Server enforces 6 digits but we let the user past 5 too — the
      // backend response is the authoritative gate. Just keep them
      // from submitting empties.
      return this.code && this.code.length >= 6
    },
    qrImageUrl() {
      if (!this.setupUrl) return ''
      // Public QR image generator — see comment in template. Keep
      // the size modest so the modal stays compact.
      const encoded = encodeURIComponent(this.setupUrl)
      return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encoded}`
    }
  },

  async mounted() {
    // Detect first-time setup: server tells us whether a secret is
    // already activated. If yes → recurring login, no QR. If no →
    // generate a new secret and show the QR.
    try {
      const has = await LoginApiService.hasTotpSecret()
      if (!has) {
        const url = await LoginApiService.getTotpSetupUrl()
        if (url) {
          this.setupUrl = url
          this.needsSetup = true
        }
      }
    } catch (e) {
      // If the probe fails just fall back to recurring-code UX —
      // the worst case is the user has to set up the authenticator
      // outside the flow and try again.
    } finally {
      this.loadingSetup = false
    }
  },

  methods: {
    async submit() {
      if (!this.canSubmit || this.busy || this.working) return
      this.localError = ''
      this.working = true
      try {
        const ok = await LoginApiService.checkTotp(this.code.trim())
        if (ok) {
          this.$emit('verified')
        } else {
          this.localError = this.t('totpInvalid')
        }
      } catch (e) {
        this.localError = (e && e.message) || this.t('errorGeneric')
      } finally {
        this.working = false
      }
    }
  }
})
</script>

<style scoped>
.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.login-header h2 { margin: 0; font-size: 1.25rem; }
.close-btn {
  border: none; background: none; font-size: 24px; cursor: pointer; color: #6b7280;
}
.close-btn:hover { color: #000; }

.form { display: flex; flex-direction: column; gap: 12px; }
.hint { margin: 0; font-size: 13px; color: #374151; line-height: 1.4; }
.loading { text-align: center; color: #6b7280; padding: 12px 0; }

.qr-wrap {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}
.qr-wrap img {
  width: 200px;
  height: 200px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.otpauth-fallback {
  word-break: break-all;
  color: #6b7280;
  font-size: 11px;
  text-align: center;
}

input {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font: inherit;
  font-size: 18px;
  letter-spacing: 0.3em;
  text-align: center;
}
button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font: inherit;
  font-size: 16px;
  cursor: pointer;
}
button:disabled { background: #c7c9d1; cursor: not-allowed; }
.error-message {
  background: #fef2f2; color: #dc2626; border: 1px solid #fecaca;
  padding: 10px 12px; border-radius: 6px; font-size: 13px;
}
</style>
